import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { datadogLogs } from '@datadog/browser-logs';

// Tracks whether `datadogRum.init` actually ran. Identity calls are no-ops when RUM
// is unconfigured (local dev, PR previews without credentials), mirroring the
// `isInitialized()` guard in src/lib/fullstory.ts.
let isRumInitialized = false;

// Only the property manager's own identity — no per-entity identifiers (job, invoice,
// property). Those are unbounded and would blow up RUM facet cardinality.
export type DatadogPropertyManagerUser = {
  propertyManagerId: number | string;
  displayName: string;
  email: string;
};

const initializeDatadog = () => {
  if (typeof window === 'undefined') return;

  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;


  if (!applicationId || !clientToken || applicationId.includes('__REPLACE') || clientToken.includes('__REPLACE')) {
    return;
  }

  // Initialize Datadog RUM
  datadogRum.init({
    applicationId,
    clientToken,
    site: process.env.NEXT_PUBLIC_DATADOG_SITE || 'datadoghq.com',
    service: process.env.NEXT_PUBLIC_DATADOG_SERVICE || 'core-customer-fe',
    env: process.env.NEXT_PUBLIC_APP_ENV || 'production',
    version: process.env.NEXT_PUBLIC_APP_VERSION,
    allowedTracingUrls: [
      'https://api.lula.local',
      'https://partnerapi.lula-dev.com',
      'https://partnerapi.lula-qa.com',
      'https://partnerapi.lula-staging.com',
      'https://partnerapi.lula-demo.com',
      'https://partnerapi.lula.io',
      {
        match: /https:\/\/partnerapi\.lula.*\.com/,
        propagatorTypes: ['tracecontext'],
      },
    ],
    sessionSampleRate: process.env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE !== undefined
      ? Number(process.env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE)
      : 100,
    sessionReplaySampleRate:
      process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE !== undefined
        ? Number(process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE)
        : 20,
    trackUserInteractions:
      process.env.NEXT_PUBLIC_DATADOG_TRACK_USER_INTERACTIONS === 'true',
    trackResources: process.env.NEXT_PUBLIC_DATADOG_TRACK_RESOURCES === 'true',
    trackLongTasks: process.env.NEXT_PUBLIC_DATADOG_TRACK_LONG_TASKS === 'true',
    defaultPrivacyLevel: 'mask-user-input',
    plugins: [reactPlugin()],
  });

  // Initialize Datadog Logs
  datadogLogs.init({
    clientToken,
    site: process.env.NEXT_PUBLIC_DATADOG_SITE || 'datadoghq.com',
    service: process.env.NEXT_PUBLIC_DATADOG_SERVICE || 'core-customer-fe',
    env: process.env.NEXT_PUBLIC_APP_ENV || 'production',
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
  });

  isRumInitialized = true;
};

/**
 * Attributes the RUM session to the signed-in property manager.
 *
 * `id`, `name` and `email` are Datadog's standard user attributes: they land on the
 * built-in `usr.*` facets rather than creating custom ones.
 */
export const identifyDatadogUser = ({
  propertyManagerId,
  displayName,
  email,
}: DatadogPropertyManagerUser) => {
  if (!isRumInitialized) return;

  datadogRum.setUser({
    id: String(propertyManagerId),
    name: displayName,
    email,
  });
};

/**
 * Drops the property manager attribution from the RUM session.
 *
 * Called on logout, and whenever the profile is absent, so that a shared machine never
 * attributes one property manager's views to the previous occupant.
 */
export const clearDatadogUser = () => {
  if (!isRumInitialized) return;

  datadogRum.clearUser();
};

export default initializeDatadog;
